import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ExportFormatCode, type SelectedClient, type SideListItem } from '@/core/models/practice.models';
import {
  formatMoney,
  type SendEmailDialogData,
  type TrialBalanceOption,
  type TrialBalanceReport,
} from '@/core/models/reports.models';
import { notifyError } from '@/core/notify/notify';
import { selectClient, selectedClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { reportsService } from '@/core/services/reports';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { openDialog } from '@/kit/dialog';
import { FullScreenButton, useReportFullScreen } from './ReportListParts';
import { SendTrialBalance } from './SendTrialBalance';

interface ClientElement {
  code: string;
  name: string;
  debit: string;
  credit: string;
  /** #180: the same account in each comparison trial balance, in the order they were added. */
  comparisons: { debit: string; credit: string }[];
}

/** "Select Reference" lists the accounting periods of the client's trial balances and filters the trial balance list. */
interface ReferenceOption {
  id: string;
  label: string;
}

/** #180/#181: at most this many trial balances can be compared with the selected one. */
const MAX_COMPARISONS = 3;

/**
 * Trial Balance report (Angular TaxreturnReporttrialbalanceComponent), `/home/taxreturns/reports-trialbalance`: pick a
 * trial balance, optionally add up to 3 comparison trial balances, Generate, export or email it.
 */
export function TaxreturnReporttrialbalance() {
  const navigate = useNavigate();
  const [initialClient] = useState(() => selectedClient());
  const client = useRef<SelectedClient | null>(initialClient);
  const mounted = useRef(true);
  /** Generate is running (stops a second click). */
  const [isGenerating, setIsGenerating] = useState(false);
  const generating = useRef(false);
  const fullScreen = useReportFullScreen();

  const [clients, setClients] = useState<SideListItem[]>([]);
  const [trialBalances, setTrialBalances] = useState<TrialBalanceOption[]>([]);
  const [references, setReferences] = useState<ReferenceOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(initialClient?.id ?? null);
  const [selectedTrialBalanceId, setSelectedTrialBalanceIdState] = useState<string | null>(null);
  const [selectedReferenceId, setSelectedReferenceIdState] = useState<string | null>(null);
  const [clientName, setClientName] = useState(initialClient?.name ?? '');
  const [totalDebit, setTotalDebit] = useState('-');
  const [totalCredit, setTotalCredit] = useState('-');
  const [dataSource, setDataSource] = useState<ClientElement[]>([]);
  /** #180/#181: trial balances to compare with the selected one ("+" adds one), shown side by side. */
  const [comparisonIds, setComparisonIds] = useState<(string | null)[]>([]);
  /** Headings and totals of the comparison columns from the last Generate. */
  const [comparisonHeaders, setComparisonHeaders] = useState<string[]>([]);
  const [comparisonTotals, setComparisonTotals] = useState<{ debit: string; credit: string }[]>([]);
  /** The trial balance the comparison lists were last refreshed for (they are refreshed on load and TB change only). */
  const [comparisonExcludes, setComparisonExcludes] = useState<string | null>(null);

  // The selections are also read from async callbacks, so they are mirrored in refs.
  const selectedTrialBalance = useRef<string | null>(null);
  const selectedReference = useRef<string | null>(null);
  const report = useRef<TrialBalanceReport | null>(null);

  const setSelectedTrialBalanceId = (id: string | null) => {
    selectedTrialBalance.current = id;
    setSelectedTrialBalanceIdState(id);
  };
  const setSelectedReferenceId = (id: string | null) => {
    selectedReference.current = id;
    setSelectedReferenceIdState(id);
  };

  /** All trial balances, or only those of the chosen accounting period ("Select Reference"). */
  const filteredTrialBalances = useMemo(
    () =>
      selectedReferenceId ? trialBalances.filter((t) => t.accountingPeriodId === selectedReferenceId) : trialBalances,
    [trialBalances, selectedReferenceId],
  );
  const comparisonOptions = useMemo(
    () => trialBalances.filter((t) => t.id !== comparisonExcludes),
    [trialBalances, comparisonExcludes],
  );

  const loadTrialBalances = async () => {
    const current = client.current;
    if (!current) {
      return;
    }
    try {
      const rows = await reportsService.getTrialBalances(current.id);
      if (!mounted.current) return;
      setTrialBalances(rows);
      setComparisonExcludes(selectedTrialBalance.current);
      const periods = new Map<string, string>();
      rows.forEach((r) => {
        if (r.accountingPeriodId && r.periodLabel && !periods.has(r.accountingPeriodId)) {
          periods.set(r.accountingPeriodId, r.periodLabel);
        }
      });
      setReferences(Array.from(periods, ([id, label]) => ({ id, label })));
    } catch (error) {
      if (mounted.current) notifyError(error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await practiceService.getClientSideList(1, 1000, '');
      if (!mounted.current) return;
      const list = response.sideModuleList;
      const current = client.current;
      if (current && !list.some((c) => c.id === current.id)) {
        list.unshift({ id: current.id, universalId: current.id, name: current.name, value: null });
      }
      setClients(list);
    } catch (error) {
      if (mounted.current) notifyError(error);
    }
  };

  useEffect(() => {
    mounted.current = true;
    if (!client.current) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return () => {
        mounted.current = false;
      };
    }
    void loadClients();
    void loadTrialBalances();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelChanges = () => {
    setSelectedTrialBalanceId(null);
    setSelectedReferenceId(null);
    setComparisonIds([]);
    setComparisonHeaders([]);
    setComparisonTotals([]);
    report.current = null;
    setDataSource([]);
    setTotalDebit('-');
    setTotalCredit('-');
    setClientName(client.current?.name ?? '');
  };

  const onClientChange = (id: string | null) => {
    setSelectedClientId(id);
    const item = clients.find((c) => c.id === id);
    if (!item || item.id === client.current?.id) {
      return;
    }
    selectClient(item.id, item.name);
    client.current = { id: item.id, name: item.name };
    cancelChanges();
    void loadTrialBalances();
  };

  const onReferenceChange = (id: string | null) => {
    setSelectedReferenceId(id);
    const filtered = id ? trialBalances.filter((t) => t.accountingPeriodId === id) : trialBalances;
    const selected = selectedTrialBalance.current;
    if (selected && !filtered.some((t) => t.id === selected)) {
      setSelectedTrialBalanceId(null);
    }
  };

  const onTrialBalanceChange = (id: string | null) => {
    setSelectedTrialBalanceId(id);
    const selected = trialBalances.find((t) => t.id === id);
    if (selected?.accountingPeriodId) {
      setSelectedReferenceId(selected.accountingPeriodId);
    }
    setComparisonIds((ids) => ids.map((existing) => (existing === id ? null : existing)));
    setComparisonExcludes(id);
  };

  /** #181: "+" adds another trial balance to compare with, rather than leaving the report to add a new one. */
  const addComparison = () => {
    if (comparisonIds.length >= MAX_COMPARISONS) {
      notifyError(`Up to ${MAX_COMPARISONS} trial balances can be compared at once.`);
      return;
    }
    setComparisonIds([...comparisonIds, null]);
  };

  const removeComparison = (index: number) => setComparisonIds((ids) => ids.filter((_, i) => i !== index));

  const generate = async () => {
    const selected = selectedTrialBalance.current;
    if (!selected) {
      notifyError('Select a trial balance to generate the report.');
      return;
    }
    const comparisons = comparisonIds.filter((id): id is string => !!id && id !== selected);
    if (generating.current) {
      return;
    }
    generating.current = true;
    setIsGenerating(true);
    try {
      const [main, ...others] = await Promise.all(
        [selected, ...comparisons].map((id) => reportsService.getTrialBalanceReport(id)),
      );
      if (!mounted.current) return;
      report.current = main;
      setClientName(main.clientName);
      // #180: one row per account code across every trial balance, with each comparison's figures alongside.
      const names = new Map<string, string>();
      [main, ...others].forEach((r) => r.rows.forEach((row) => names.has(row.code) || names.set(row.code, row.name)));
      const codes = Array.from(names.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      const figures = (r: TrialBalanceReport, code: string) => {
        const row = r.rows.find((x) => x.code === code);
        return { debit: formatMoney(row?.debit ?? 0), credit: formatMoney(row?.credit ?? 0) };
      };
      setDataSource(
        codes.map((code) => ({
          code,
          name: names.get(code) ?? '',
          ...figures(main, code),
          comparisons: others.map((o) => figures(o, code)),
        })),
      );
      setTotalDebit(formatMoney(main.totalDebit));
      setTotalCredit(formatMoney(main.totalCredit));
      setComparisonHeaders(others.map((o) => o.refNo));
      setComparisonTotals(
        others.map((o) => ({ debit: formatMoney(o.totalDebit), credit: formatMoney(o.totalCredit) })),
      );
    } catch (error) {
      if (mounted.current) notifyError(error);
    } finally {
      generating.current = false;
      if (mounted.current) setIsGenerating(false);
    }
  };

  const exportReport = async (format: ExportFormatCode) => {
    const current = report.current;
    if (!current) {
      notifyError('Generate the report before exporting it.');
      return;
    }
    try {
      await reportsService.exportTrialBalanceReport(current.trialBalanceId, format);
    } catch (error) {
      notifyError(error);
    }
  };

  const openSendTrial = () => {
    const current = report.current;
    if (!current) {
      notifyError('Generate the report before sending it.');
      return;
    }
    const data: SendEmailDialogData = {
      title: 'Send Trial Balance',
      draft: current.email,
      send: (payload) => reportsService.sendTrialBalance(current.trialBalanceId, payload),
      downloadAttachment: () => reportsService.exportTrialBalanceReport(current.trialBalanceId, ExportFormatCode.Pdf),
    };
    openDialog(SendTrialBalance, { data });
  };

  const columns: DataTableColumn<ClientElement>[] = [
    {
      id: 'code',
      header: 'Code',
      headerClassName: 'w-100',
      cell: (element) => element.code,
      footer: ' ',
    },
    {
      id: 'name',
      header: 'Account Name',
      cell: (element) => element.name,
      footer: ' Total ',
      footerClassName: 'text-align-right',
    },
    {
      id: 'debit',
      header: 'Debit (£ - GBP)',
      headerClassName: 'w-200 text-align-right',
      cellClassName: 'text-align-right',
      footerClassName: 'text-align-right',
      cell: (element) => element.debit,
      footer: totalDebit,
    },
    {
      id: 'credit',
      header: 'Credit (£ - GBP)',
      headerClassName: 'w-200 text-align-right',
      cellClassName: 'text-align-right',
      footerClassName: 'text-align-right',
      cell: (element) => element.credit,
      footer: totalCredit,
    },
    ...comparisonHeaders.flatMap((header, c): DataTableColumn<ClientElement>[] => [
      {
        id: `cmpDebit${c}`,
        header: `${header} Debit`,
        headerClassName: 'w-200 text-align-right',
        cellClassName: 'text-align-right',
        footerClassName: 'text-align-right',
        cell: (element) => element.comparisons[c]?.debit,
        footer: comparisonTotals[c]?.debit,
      },
      {
        id: `cmpCredit${c}`,
        header: `${header} Credit`,
        headerClassName: 'w-200 text-align-right',
        cellClassName: 'text-align-right',
        footerClassName: 'text-align-right',
        cell: (element) => element.comparisons[c]?.credit,
        footer: comparisonTotals[c]?.credit,
      },
    ]),
  ];

  return (
    <SideMenuPage menu="reports">
      {/* Design review 2 Nov 2026: Send / Cancel Changes sit in the sticky page header, not a fixed footer. */}
      <PageHeader title="Trial Balance">
        <Button className="action-button primary-button" busy={isGenerating} onClick={() => void generate()}>
          <Icon name="sync" />
          <span>Generate</span>
        </Button>
        <Button className="action-button secondary-button" onClick={cancelChanges}>
          Cancel Changes
        </Button>
        <Button className="action-button primary-button" onClick={openSendTrial}>
          Send
        </Button>
      </PageHeader>
      <div className={`content-body${fullScreen.className}`}>
        <div className="content-header mb-0">
          <div className="content-header-right mb-1">
            <div className="text-gray font-size-14 fw-bold">
              Select Trial Balance<span className="text-red">*</span>
            </div>
            <Select
              className="w-270"
              items={filteredTrialBalances}
              bindLabel="label"
              bindValue="id"
              placeholder="Select Trial Balance"
              value={selectedTrialBalanceId}
              onChange={(value) => onTrialBalanceChange(value as string | null)}
            />
            <Select
              className="w-270"
              items={references}
              bindLabel="label"
              bindValue="id"
              placeholder="Select Reference"
              value={selectedReferenceId}
              onChange={(value) => onReferenceChange(value as string | null)}
            />
            {/* #180/#181: "+" adds a trial balance to compare with the selected one. */}
            {comparisonIds.map((id, index) => (
              <Select
                key={index}
                className="w-270"
                items={comparisonOptions}
                bindLabel="label"
                bindValue="id"
                placeholder="Compare with"
                value={id}
                onChange={(value) => {
                  if (value === null) {
                    removeComparison(index);
                  } else {
                    setComparisonIds((ids) => ids.map((existing, i) => (i === index ? (value as string) : existing)));
                  }
                }}
              />
            ))}
            <Button className="light-bg" title="Compare with another trial balance" onClick={addComparison}>
              <Icon name="add" />
            </Button>
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={clients}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              value={selectedClientId}
              onChange={(value) => onClientChange(value as string | null)}
            />
          </div>
        </div>
        <div className="content-header pt-1 mb-0 hr-top">
          <div className="content-header-left mb-1">
            <div className="text-gray font-size-18 fw-normal">
              Generated Report for : <span className="fw-bold">{clientName}</span>
            </div>
          </div>
          <div className="content-header-right mb-1">
            <div className="toolbar">
              <Button title="Export to Excel" onClick={() => void exportReport(ExportFormatCode.Excel)}>
                <Icon name="border_all" className="icon-color-xls" />
              </Button>
              <Button title="Export to CSV" onClick={() => void exportReport(ExportFormatCode.Csv)}>
                <Icon name="description" outlined className="icon-color-xls" />
              </Button>
              <Button title="Export to PDF" onClick={() => void exportReport(ExportFormatCode.Pdf)}>
                <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
              </Button>
              <FullScreenButton fullScreen={fullScreen.fullScreen} onClick={fullScreen.toggle} />
            </div>
          </div>
        </div>

        <div className="sidenav-table sidenav-table-input wp-100">
          <div className="mat-table-wrapper contact-detail-table no-hover">
            <DataTable stickyHeader columns={columns} rows={dataSource} rowKey={(row) => row.code} showFooter />
          </div>
        </div>
      </div>
    </SideMenuPage>
  );
}
